import { REACT_QUERY_KEYS, bridgeRequest } from 'common'
import { logBridgeError, sendScreenView } from 'common/helpers'
import { Routes } from 'common/constants'
import { TEndpoint, TEntity } from 'contexts/app-context/types'
import { entityPayloadMap } from 'contexts/app-context/types/app-context/components/utils'

import { queryOptions } from '@tanstack/react-query'

import { TServiceItems } from '../../types/MyServices.types'

interface GetServicesQueryParams {
  endpoint?: TEndpoint
  userCountry?: string
  userSelectedEntity?: TEntity
  isUSBranch: boolean
}

const sendGetRequestsDurationEvent = async (scenario: string) => {
  try {
    await sendScreenView({
      flow: 'get_requests',
      step: 'get_requests',
      scenario,
      event_name: 'async_event',
      screen_name: Routes.MY_SERVICE_REQUESTS,
    })
  } catch (error: unknown) {
    logBridgeError('send get requests duration event', error)
  }
}

export const getServicesQueryKey = ({
  userCountry,
  userSelectedEntity,
}: Pick<GetServicesQueryParams, 'userCountry' | 'userSelectedEntity'>) => [
  REACT_QUERY_KEYS.SUPPORT,
  userCountry,
  userSelectedEntity,
]

export const getServicesQueryOptions = ({
  endpoint,
  userCountry,
  userSelectedEntity,
  isUSBranch,
}: GetServicesQueryParams) => {
  const isQueryEnabled = !!endpoint && !!userCountry && !!userSelectedEntity

  return queryOptions({
    queryKey: getServicesQueryKey({ userCountry, userSelectedEntity }),
    queryFn: () => {
      const startedAt = performance.now()

      if (!endpoint || !userCountry || !userSelectedEntity) {
        throw new Error('No endpoint or user data available (useGetServices)')
      }

      const customHeaders = {
        'x-inter-user-org': userCountry,
        'x-inter-account-org': entityPayloadMap[userSelectedEntity],
      }

      return bridgeRequest<TServiceItems[] | string>({
        endpoint: {
          ...endpoint,
          headers: { ...endpoint.headers, ...customHeaders },
        },
        returnAllResponse: false,
        isUSBranch,
      }).finally(() => {
        const endedAt = performance.now()
        const requestDuration = endedAt - startedAt

        sendGetRequestsDurationEvent(
          JSON.stringify({
            getServices: endpoint,
            isQueryEnabled,
            isUSBranch,
            customHeaders,
            userSelectedEntity,
            userCountry,
            startedAt,
            endedAt,
            requestDuration,
          }),
        )
      })
    },
    enabled: isQueryEnabled,
    staleTime: Infinity,
    gcTime: Infinity,
  })
}
